import { ExternalLink, ScanEye } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { InsuranceIndication } from '../../models/insuranceIndication'
import { domSnapshots, screenshots } from './checkoutEvidenceRows'
import { CheckoutEvidenceSummary } from './CheckoutEvidenceSummary'

export interface CheckoutEvidenceDialogProps {
  indication: InsuranceIndication
}

/**
 * Cai-admin-only dialog showing the page the customer checked out from — the screenshot
 * taken at the click and the snapshot of the page behind it. This is the same capture
 * Corgi keeps as the record of a bind.
 */
export function CheckoutEvidenceDialog({ indication }: CheckoutEvidenceDialogProps) {
  const evidence = indication.checkoutEvidence
  if (!evidence) return null

  const images = screenshots(evidence)
  const snapshots = domSnapshots(evidence)

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="link" className="h-auto justify-start px-0">
          Checkout capture
          <ScanEye className="size-3" />
        </Button>
      </DialogTrigger>
      <DialogContent size="xl" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>What the customer saw at checkout</DialogTitle>
        </DialogHeader>

        <CheckoutEvidenceSummary evidence={evidence} />

        {snapshots.length > 0 && (
          <div className="flex flex-col items-start gap-1">
            {snapshots.map((snapshot) => (
              <Button
                key={snapshot.id}
                size="sm"
                variant="link"
                className="h-auto justify-start px-0"
                href={snapshot.url ?? undefined}
                target="_blank"
              >
                Open the page as it was ({new Date(snapshot.dateCreated).toLocaleString()})
                <ExternalLink className="size-3" />
              </Button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-6">
          {images.map((image) => (
            <div key={image.id} className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">
                {new Date(image.dateCreated).toLocaleString()}
              </span>
              {image.url && (
                <a href={image.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={image.url}
                    alt={image.fileName}
                    loading="lazy"
                    className="w-full rounded-md border"
                  />
                </a>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
